import { useEffect, useState, useCallback } from 'react';
import { Printer, Plus, Check, Trash2, RefreshCw, Package, Wrench, ChevronDown, ChevronUp, AlertCircle, X } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

const CALIBERS = [
  '.22 LR', '.22-250', '.223 / 5.56', '.243 Win', '.25-06', '.257 Roberts', '.260 Rem',
  '.270 Win', '.270 WSM', '.280 Rem', '.30-06', '.300 BLK', '.300 WSM', '.300 Win Mag',
  '.308 Win / 7.62', '.338 Win Mag', '.338 Lapua', '.35 Whelen', '.375 H&H', '.416 Rigby',
  '.45 ACP', '.50 BMG', '6mm Creedmoor', '6.5 Creedmoor', '6.5 PRC', '6.5mm',
  '7mm-08', '7mm Rem Mag', '7mm WSM', '7mm', '7.62x39 (AK)', '9mm', '12 Gauge', 'Other / Custom',
];
const COLORS = [
  'Smooth Stainless',
  'Graphite Black Cerakote',
  'FDE – Flat Dark Earth Cerakote',
];
const PITCHES = [
  '1/2-20', '1/2-28', '1/2-36',
  '9/16-24', '9/16-28',
  '.578-28 (37/64-28)',
  '5/8-24',
  '49/64-20',
  '3/4-24',
  'M14-1 RH', 'M14-1 LH',
  'M15-.75', 'M15-1',
  'M18-1', 'M22-.75',
  'Other',
];

const inputCls = "bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors placeholder-zinc-600";

// ── Add Job Form ──────────────────────────────────────────────────────────────
function AddJobForm({ onAdded, onCancel, token }) {
  const [type, setType] = useState('clamp_on');
  const [form, setForm] = useState({ order_number: '', customer_name: '', quantity: 1, caliber: CALIBERS[0], color: COLORS[0], notes: '', diameter_muzzle: '', diameter_back: '', thread_pitch: PITCHES[0] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const body = {
        product_type: type, ...form,
        quantity: +form.quantity,
        diameter_muzzle: form.diameter_muzzle ? +form.diameter_muzzle : null,
        diameter_back: form.diameter_back ? +form.diameter_back : null,
      };
      const res = await fetch(`${BASE}/api/admin/cutsheet/muzzle-brake/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      onAdded();
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSave} className="bg-[#1a1a1a] border border-orange-500/30 p-5 space-y-3">
      <div className="flex items-center gap-3 mb-2">
        <h3 className="font-bebas text-base text-white tracking-wider">ADD JOB TO QUEUE</h3>
        <div className="flex gap-2">
          {[['clamp_on','Clamp-On'], ['threaded','Threaded']].map(([val,lbl]) => (
            <button key={val} type="button" onClick={() => setType(val)}
              className={`font-inter text-xs px-3 py-1 border transition-colors ${type === val ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500'}`}>
              {lbl}
            </button>
          ))}
        </div>
        <button type="button" onClick={onCancel} className="ml-auto text-zinc-500 hover:text-white"><X size={16} /></button>
      </div>

      {error && <div className="text-red-400 font-inter text-xs">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Order #</label>
          <input value={form.order_number} onChange={set('order_number')} className={`${inputCls} w-full`} placeholder="WM-XXXXXXXX" /></div>
        <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Customer</label>
          <input value={form.customer_name} onChange={set('customer_name')} className={`${inputCls} w-full`} placeholder="Name (optional)" /></div>
        <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Qty</label>
          <input type="number" min="1" value={form.quantity} onChange={set('quantity')} className={`${inputCls} w-full`} /></div>

        {type === 'clamp_on' && (<>
          <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Muzzle Ø (inches) *</label>
            <input type="number" step="0.001" required value={form.diameter_muzzle} onChange={set('diameter_muzzle')} className={`${inputCls} w-full font-mono`} placeholder="0.580" /></div>
          <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Back Ø — 1.5" back</label>
            <input type="number" step="0.001" value={form.diameter_back} onChange={set('diameter_back')} className={`${inputCls} w-full font-mono`} placeholder="0.600" /></div>
        </>)}

        {type === 'threaded' && (
          <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Thread Pitch *</label>
            <select value={form.thread_pitch} onChange={set('thread_pitch')} className={`${inputCls} w-full`} required>
              <optgroup label='1" Blank — 1/2" thread family'>
                {['1/2-20','1/2-28','1/2-36','9/16-24','9/16-28'].map(p => <option key={p} value={p}>{p}</option>)}
              </optgroup>
              <optgroup label='1-1/8" Blank — 5/8"+ & metric family'>
                {['.578-28 (37/64-28)','5/8-24','49/64-20','3/4-24','M14-1 RH','M14-1 LH','M15-.75','M15-1','M18-1','M22-.75','Other'].map(p => <option key={p} value={p}>{p}</option>)}
              </optgroup>
            </select></div>
        )}

        <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Caliber</label>
          <select value={form.caliber} onChange={set('caliber')} className={`${inputCls} w-full`}>
            {CALIBERS.map(c => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Color</label>
          <select value={form.color} onChange={set('color')} className={`${inputCls} w-full`}>
            {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div className="col-span-2 sm:col-span-3">
          <label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Notes</label>
          <input value={form.notes} onChange={set('notes')} className={`${inputCls} w-full`} placeholder="Special instructions..." /></div>
      </div>

      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={saving}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-base tracking-widest px-5 py-2 transition-colors">
          <Plus size={14} />{saving ? 'ADDING...' : 'ADD TO QUEUE'}
        </button>
        <button type="button" onClick={onCancel} className="border border-white/20 text-zinc-400 hover:text-white font-bebas text-base tracking-widest px-4 py-2 transition-colors">CANCEL</button>
      </div>
    </form>
  );
}

// ── Job Row ───────────────────────────────────────────────────────────────────
function JobRow({ job, onComplete, onDelete, selected, onToggleSelect }) {
  const isClamp = job.product_type === 'clamp_on';
  const done = job.status === 'completed';
  return (
    <div className={`grid items-center gap-2 px-3 py-2.5 border-b border-white/5 text-sm font-inter transition-colors ${done ? 'opacity-40 bg-green-500/3' : 'hover:bg-white/2'} ${isClamp ? 'grid-cols-[24px_24px_1fr_80px_80px_100px_100px_60px_60px]' : 'grid-cols-[24px_24px_1fr_120px_100px_100px_60px_60px]'}`}>
      <input type="checkbox" checked={selected} onChange={onToggleSelect} className="accent-orange-500" />
      <div className={`text-base leading-none ${done ? 'text-green-500' : 'text-zinc-600'}`}>{done ? '✓' : '□'}</div>
      <div>
        <span className="font-semibold text-white">{job.order_number}</span>
        {job.customer_name && job.customer_name !== '—' && <span className="text-[10px] text-zinc-600 ml-2">{job.customer_name}</span>}
      </div>
      {isClamp ? (<>
        <div className="font-mono text-orange-300 font-bold">{job.diameter_muzzle ? `${Number(job.diameter_muzzle).toFixed(3)}"` : '—'}</div>
        <div className="font-mono text-zinc-400">{job.diameter_back ? `${Number(job.diameter_back).toFixed(3)}"` : '—'}</div>
      </>) : (
        <div className="font-bold text-orange-300">{job.thread_pitch}</div>
      )}
      <div className="text-zinc-300">{job.caliber}</div>
      <div className="text-zinc-400 text-xs">{job.color}</div>
      <div className="font-bold text-white text-center">{job.quantity}</div>
      <div className="flex gap-1 justify-end">
        {!done && <button onClick={() => onComplete(job.job_id)} className="p-1 text-green-500 hover:text-white transition-colors" title="Mark complete"><Check size={13} /></button>}
        <button onClick={() => onDelete(job.job_id)} className="p-1 text-zinc-700 hover:text-red-400 transition-colors" title="Delete job"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

// ── Group Section ─────────────────────────────────────────────────────────────
function GroupSection({ title, jobs, onComplete, onDelete, selected, onToggleSelect, bgColor = '#222' }) {
  const [open, setOpen] = useState(true);
  const isClamp = jobs[0]?.product_type === 'clamp_on';
  const pending = jobs.filter(j => j.status === 'pending').length;
  const done = jobs.filter(j => j.status === 'completed').length;

  return (
    <div className="border border-white/10 overflow-hidden mb-3">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        style={{ background: bgColor }}>
        <div className="flex items-center gap-3">
          <span className="font-bebas text-base text-white tracking-wider">{title}</span>
          <span className="font-inter text-xs text-zinc-400">{pending} pending{done > 0 ? ` · ${done} done` : ''}</span>
        </div>
        {open ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />}
      </button>
      {open && (
        <>
          {/* Column headers */}
          <div className={`grid gap-2 px-3 py-1.5 bg-[#141414] border-b border-white/10 font-inter text-[9px] text-zinc-600 uppercase tracking-wider ${isClamp ? 'grid-cols-[24px_24px_1fr_80px_80px_100px_100px_60px_60px]' : 'grid-cols-[24px_24px_1fr_120px_100px_100px_60px_60px]'}`}>
            <div></div><div></div><div>Order #</div>
            {isClamp ? (<><div>Muzzle Ø</div><div>Back Ø</div></>) : <div>Thread</div>}
            <div>Caliber</div><div>Color</div><div>Qty</div><div></div>
          </div>
          {jobs.map(job => (
            <JobRow key={job.job_id} job={job}
              onComplete={onComplete} onDelete={onDelete}
              selected={selected.includes(job.job_id)}
              onToggleSelect={() => onToggleSelect(job.job_id)} />
          ))}
        </>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminCutSheetEngine() {
  const { token } = useAdminAuth();
  const [tab, setTab] = useState('muzzle_brake');
  const [queue, setQueue] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkCompleting, setBulkCompleting] = useState(false);
  const [generatingBatch, setGeneratingBatch] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [error, setError] = useState('');

  const loadMuzzleBrake = useCallback(async () => {
    const res = await fetch(`${BASE}/api/admin/cutsheet/muzzle-brake/queue`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setQueue(await res.json());
  }, [token]);

  const loadSuppressor = useCallback(async () => {
    const [inv, bat] = await Promise.all([
      fetch(`${BASE}/api/admin/cutsheet/suppressor/inventory-targets`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch(`${BASE}/api/admin/cutsheet/suppressor/batches`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
    ]);
    setInventory(Array.isArray(inv) ? inv : []);
    setBatches(Array.isArray(bat) ? bat : []);
  }, [token]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadMuzzleBrake(), loadSuppressor()]).finally(() => setLoading(false));
  }, [loadMuzzleBrake, loadSuppressor]);

  const handleComplete = async (jobId) => {
    await fetch(`${BASE}/api/admin/cutsheet/muzzle-brake/jobs/${jobId}/complete`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    loadMuzzleBrake();
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;
    await fetch(`${BASE}/api/admin/cutsheet/muzzle-brake/jobs/${jobId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    loadMuzzleBrake();
  };

  const handleBulkComplete = async () => {
    if (selected.length === 0) return;
    setBulkCompleting(true);
    await fetch(`${BASE}/api/admin/cutsheet/muzzle-brake/jobs/bulk-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ job_ids: selected }),
    });
    setSelected([]);
    loadMuzzleBrake();
    setBulkCompleting(false);
  };

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const res = await fetch(`${BASE}/api/admin/cutsheet/muzzle-brake/queue/pdf?mode=plain`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('PDF failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `WM_MuzzleBrakeCutSheet_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    setPrintLoading(false);
  };

  const handleGenerateBatch = async () => {
    setGeneratingBatch(true);
    try {
      const res = await fetch(`${BASE}/api/admin/cutsheet/suppressor/generate-batch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      alert(data.message || 'Batch generated');
      loadSuppressor();
    } catch (e) { alert(e.message); }
    setGeneratingBatch(false);
  };

  const handlePrintBatch = async (batchId) => {
    const res = await fetch(`${BASE}/api/admin/cutsheet/suppressor/batches/${batchId}/pdf?mode=plain`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `WM_SuppressorBatch_${batchId}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  const handleCompleteBatch = async (batchId) => {
    await fetch(`${BASE}/api/admin/cutsheet/suppressor/batches/${batchId}/complete`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    loadSuppressor();
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;

  const allPendingIds = queue?.all_jobs?.filter(j => j.status === 'pending').map(j => j.job_id) || [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider flex items-center gap-3">
            <Wrench size={24} className="text-orange-500" />CUT SHEET ENGINE
          </h1>
          <p className="font-inter text-xs text-zinc-500 mt-1">Two separate production systems — Muzzle Brakes (order-driven) and Suppressors (inventory-driven)</p>
        </div>
        <button onClick={() => { loadMuzzleBrake(); loadSuppressor(); }} className="flex items-center gap-2 border border-white/20 text-zinc-400 hover:text-white font-inter text-xs px-3 py-2 transition-colors">
          <RefreshCw size={13} />Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {[
          { key: 'muzzle_brake', label: `Muzzle Brakes (${queue?.total_pending || 0} pending)` },
          { key: 'suppressors',  label: `Suppressors — Batch` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`font-inter text-sm px-5 py-2.5 border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: MUZZLE BRAKES ── */}
      {tab === 'muzzle_brake' && (
        <div className="space-y-4">
          {/* Action bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={handlePrint} disabled={printLoading || (queue?.total_pending || 0) === 0}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bebas text-base tracking-widest px-5 py-2.5 transition-colors">
              <Printer size={16} />{printLoading ? 'GENERATING...' : `PRINT TODAY'S CUT SHEET (${queue?.total_pending || 0})`}
            </button>
            <button onClick={() => { setShowAddForm(f => !f); }}
              className="flex items-center gap-2 border border-white/20 hover:border-orange-500/40 text-zinc-400 hover:text-white font-bebas text-base tracking-widest px-4 py-2.5 transition-colors">
              <Plus size={14} />ADD JOB
            </button>
            {selected.length > 0 && (
              <button onClick={handleBulkComplete} disabled={bulkCompleting}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-60 text-white font-bebas text-base tracking-widest px-4 py-2.5 transition-colors ml-auto">
                <Check size={14} />{bulkCompleting ? 'COMPLETING...' : `MARK ${selected.length} COMPLETE`}
              </button>
            )}
            {selected.length === 0 && allPendingIds.length > 0 && (
              <button onClick={() => setSelected(allPendingIds)}
                className="font-inter text-xs text-zinc-500 hover:text-white ml-auto transition-colors">
                Select all {allPendingIds.length}
              </button>
            )}
            {selected.length > 0 && (
              <button onClick={() => setSelected([])} className="font-inter text-xs text-zinc-500 hover:text-white transition-colors">
                Clear selection
              </button>
            )}
          </div>

          {showAddForm && (
            <AddJobForm token={token} onAdded={() => { setShowAddForm(false); loadMuzzleBrake(); }} onCancel={() => setShowAddForm(false)} />
          )}

          {/* How-to note */}
          <div className="bg-[#1a1a1a] border border-white/10 px-4 py-3 font-inter text-xs text-zinc-500">
            <strong className="text-zinc-400">Workflow:</strong> Print daily → machinist works top-to-bottom → checks off each item → returns sheet to front office → admin marks complete here
          </div>

          {/* SECTION A: Clamp-On */}
          <div>
            <div className="font-bebas text-lg text-white tracking-widest border-b border-white/10 pb-2 mb-3 flex items-center gap-3">
              SECTION A — CLAMP-ON MUZZLE BRAKES
              <span className="font-inter text-xs text-zinc-500 font-normal">{queue?.clamp_on_pending || 0} pending</span>
            </div>
            {queue?.clamp_on_groups && Object.values(queue.clamp_on_groups).map(g => g.jobs.length > 0 && (
              <GroupSection key={g.meta.group} title={g.meta.label} jobs={g.jobs}
                onComplete={handleComplete} onDelete={handleDelete}
                selected={selected} onToggleSelect={toggleSelect}
                bgColor={['#1a1a1a','#1e1a14','#14181a','#1a1418'][g.meta.group - 1]} />
            ))}
            {(queue?.clamp_on_pending || 0) === 0 && (
              <div className="text-center py-8 bg-[#1a1a1a] border border-white/10 text-zinc-600 font-inter text-sm">No clamp-on jobs pending — queue is clear</div>
            )}
          </div>

          {/* SECTION B: Threaded — 2 blank size groups */}
          <div>
            <div className="font-bebas text-lg text-white tracking-widest border-b border-white/10 pb-2 mb-3 flex items-center gap-3">
              SECTION B — THREADED MUZZLE BRAKES
              <span className="font-inter text-xs text-zinc-500 font-normal">{queue?.threaded_pending || 0} pending · 2 blank sizes</span>
            </div>
            {queue?.threaded_groups && Object.values(queue.threaded_groups).map(tg => tg.jobs.length > 0 && (
              <GroupSection
                key={tg.meta.group}
                title={tg.meta.label}
                jobs={tg.jobs}
                onComplete={handleComplete}
                onDelete={handleDelete}
                selected={selected}
                onToggleSelect={toggleSelect}
                bgColor="#2a2a2a"
              />
            ))}
            {(queue?.threaded_pending || 0) === 0 && (
              <div className="text-center py-8 bg-[#1a1a1a] border border-white/10 text-zinc-600 font-inter text-sm">No threaded jobs pending — queue is clear</div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: SUPPRESSORS ── */}
      {tab === 'suppressors' && (
        <div className="space-y-5">
          {/* Inventory targets */}
          <div>
            <div className="font-bebas text-lg text-white tracking-widest border-b border-white/10 pb-2 mb-3">INVENTORY TARGETS vs CURRENT STOCK</div>
            <div className="bg-[#1a1a1a] border border-white/10 overflow-hidden">
              <div className="grid grid-cols-[2fr_80px_80px_80px_100px] gap-0 px-4 py-2 bg-[#141414] border-b border-white/10 font-inter text-[9px] text-zinc-500 uppercase tracking-wider">
                <div>Product</div><div>Target</div><div>In Stock</div><div>Deficit</div><div>Action</div>
              </div>
              {inventory.map(item => (
                <div key={item.sku} className={`grid grid-cols-[2fr_80px_80px_80px_100px] gap-0 px-4 py-3 border-b border-white/5 font-inter text-sm ${item.needs_build ? 'bg-orange-500/3' : ''}`}>
                  <div>
                    <p className="text-white font-medium">{item.product_name}</p>
                    <p className="text-[10px] text-zinc-600 font-mono">{item.sku}</p>
                  </div>
                  <div className="text-zinc-400 font-mono">{item.target_qty}</div>
                  <div className={`font-mono font-bold ${item.current_qty < item.target_qty ? 'text-red-400' : 'text-green-400'}`}>{item.current_qty}</div>
                  <div className={`font-mono font-bold ${item.deficit > 0 ? 'text-orange-400' : 'text-zinc-600'}`}>{item.deficit > 0 ? `-${item.deficit}` : '—'}</div>
                  <div>{item.needs_build && <span className="font-inter text-[10px] text-orange-400 border border-orange-500/30 px-2 py-0.5 uppercase tracking-wider">Build needed</span>}</div>
                </div>
              ))}
              {inventory.length === 0 && <div className="text-center py-8 text-zinc-600 font-inter text-sm">No suppressor inventory targets configured</div>}
            </div>
          </div>

          {/* Generate batch */}
          <div className="flex items-center gap-3">
            <button onClick={handleGenerateBatch} disabled={generatingBatch}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-base tracking-widest px-6 py-2.5 transition-colors">
              <Package size={16} />{generatingBatch ? 'GENERATING...' : 'GENERATE PRODUCTION BATCH'}
            </button>
            <p className="font-inter text-xs text-zinc-500">Creates a batch for all SKUs below target. Machinists build to stock.</p>
          </div>

          {/* Batch history */}
          <div>
            <div className="font-bebas text-lg text-white tracking-widest border-b border-white/10 pb-2 mb-3">BATCH HISTORY</div>
            {batches.length === 0 ? (
              <div className="text-center py-8 bg-[#1a1a1a] border border-white/10 text-zinc-600 font-inter text-sm">No batches generated yet</div>
            ) : (
              <div className="space-y-2">
                {batches.map(b => (
                  <div key={b.batch_id} className={`bg-[#1a1a1a] border border-white/10 p-4 flex items-center justify-between ${b.status === 'completed' ? 'opacity-60' : ''}`}>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-bebas text-base text-white tracking-wider">{b.batch_id}</span>
                        <span className={`font-inter text-[10px] px-2 py-0.5 border uppercase tracking-wider ${b.status === 'completed' ? 'text-green-400 border-green-500/30 bg-green-500/10' : 'text-orange-400 border-orange-500/30 bg-orange-500/10'}`}>
                          {b.status}
                        </span>
                      </div>
                      <p className="font-inter text-xs text-zinc-500 mt-0.5">
                        {b.total_units} units · {b.items?.length || 0} SKUs · {b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handlePrintBatch(b.batch_id)}
                        className="flex items-center gap-1 font-inter text-xs border border-white/20 hover:border-orange-500/40 text-zinc-400 hover:text-white px-3 py-1.5 transition-colors">
                        <Printer size={11} />Print
                      </button>
                      {b.status === 'pending' && (
                        <button onClick={() => handleCompleteBatch(b.batch_id)}
                          className="flex items-center gap-1 font-inter text-xs bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 transition-colors">
                          <Check size={11} />Complete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
